import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Eye, Trash2, PenLine, Info, Archive, ArchiveRestore } from "lucide-react";
import { noImg } from "@/assets";
import { Link } from "react-router-dom";
import { useState } from "react";
import {
  DeleteConfirmDialog,
  ItemsDialog,
  DetailsDialog,
  DayAndDateDialog,
  ImageDialog,
  ArchiveConfirmDialog,
} from "@/components/dialog/common-dialog";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import {
  deleteCourseById,
  clearCourseMessages,
  updateExistingCourse,
  fetchAllCourses,
} from "@/redux/slice/menu-manage-slice/courses-slice";
import hasPermission from "@/hooks/hasPermission";
import { Switch } from "@/components/ui/switch";
import { toggleItemStatus } from "@/api/new-api/staff-management/menu-manage/toggle-status";
import useAuth from "@/hooks/useAuth";

function CourseListTable({ courses }) {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { deleteLoading, updateLoading, requestStatus, error } = useSelector(
    (state) => state.menuManage.course
  );
  const [archiveDialog, setArchiveDialog] = useState(false);
  const [archiveActionType, setArchiveActionType] = useState("archive");
  const [itemDialog, setItemDialog] = useState(null);
  const [comboImageDialog, setComboImageDialog] = useState(false);
  const [itemDetails, setItemsDetails] = useState(false);
  const [dayAndDateDialog, setDayAndDateDialog] = useState(false);
  const [comboMealDetails, setComboMealDetails] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);

  const closeArchiveDialog = () => {
    setArchiveDialog(false);
    setItemDialog(null);
  };
  const closeImageDialog = () => {
    setComboImageDialog(false);
    setItemDialog(null);
  };

  const closeItemDetails = () => {
    setItemsDetails(false);
  };
  const closeDayAndDateDialog = () => {
    setDayAndDateDialog(false);
  };
  const closeComboMealDetails = () => {
    setComboMealDetails(false);
  };

  const archiveHandler = async (id, item, type) => {
    try {
      if (!id) {
        toast.error("ID is required.");
        return;
      }
      dispatch(clearCourseMessages());
      const newStatus = type === "archive" ? "archived" : "active";
      const res = await dispatch(
        updateExistingCourse({
          id,
          data: {
            ...item,
            status: newStatus,
          },
        })
      );
      if (updateExistingCourse.fulfilled.match(res)) {
        toast.success(`Course archived successfully!`);
      } else if (updateExistingCourse.rejected.match(res)) {
        toast.error(res?.payload || `Failed to archive course.`);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    } finally {
      closeArchiveDialog();
    }
  };

  const deleteHandler = async (id) => {
    try {
      if (!id) {
        toast.error("ID is required.");
        return;
      }
      dispatch(clearCourseMessages());
      const res = await dispatch(deleteCourseById(id));
      if (deleteCourseById.fulfilled.match(res)) {
        toast.success(`Course deleted successfully!`);
      } else if (deleteCourseById.rejected.match(res)) {
        toast.error(res?.payload || `Failed to delete course.`);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    } finally {
      setDeleteDialog(false);
      setItemDialog(null);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const res = await toggleItemStatus("courses", id);
      if (res.status === "success") {
        toast.success(res.message || "Course status updated successfully");
        dispatch(fetchAllCourses(user?.restaurant_id));
      } else {
        toast.error(res.message || "Failed to toggle status.");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    }
  };

  return (
    <Table className="w-full">
      <TableHeader>
        <TableRow>
          <TableHead className="w-[130px]">Course Name</TableHead>
          <TableHead>Image</TableHead>
          <TableHead>Items</TableHead>
          <TableHead>In Price</TableHead>
          <TableHead>Out Price</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {courses && courses.length > 0 ? (
          courses.map((item) => (
            <TableRow key={item?.id}>
              <TableCell className="font-medium">{item?.name}</TableCell>
              <TableCell>
                <div
                  onClick={() => {
                    setComboImageDialog(true);
                    setItemDialog(item);
                  }}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <img
                    src={item?.file_manager_url || noImg}
                    alt={item?.name}
                    className="w-8 h-8 rounded-lg"
                  />
                </div>
              </TableCell>
              <TableCell>
                <span className="flex gap-1 items-center">
                  {item?.course_items?.length}
                  <Eye
                    className="cursor-pointer"
                    onClick={() => {
                      setItemsDetails(true);
                      setItemDialog(item);
                    }}
                    size={18}
                  />
                </span>
              </TableCell>
              <TableCell>${item?.in_price || "0"}</TableCell>
              <TableCell>${item?.out_price || "0"}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {item?.status === "active" ? (
                    <>
                      <span className="text-cgreen text-base">Active</span>
                      <Switch
                        checked={true}
                        onCheckedChange={() => handleToggleStatus(item.id)}
                      />
                    </>
                  ) : item?.status === "inactive" ? (
                    <>
                      <span className="text-cred text-base">Inactive</span>
                      <Switch
                        checked={false}
                        onCheckedChange={() => handleToggleStatus(item.id)}
                      />
                    </>
                  ) : item?.status === "archived" ? (
                    <span className="text-cred text-base">Archived</span>
                  ) : (
                    <span className="text-cred text-base">Draft</span>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right justify-end flex gap-2 items-center">
                <div className="flex items-center gap-2 h-8">
                  <Info
                    onClick={() => {
                      setComboMealDetails(true);
                      setItemDialog(item);
                    }}
                    className="text-cwhite3 cursor-pointer"
                    size={18}
                  />

                  {hasPermission("back_office.menu_management.update_course") && item?.status !== "archived" && (
                    <Link
                      to={"/backoffice/menu/course/update"}
                      state={item}
                      className="cursor-pointer"
                    >
                      <PenLine className="text-cyellow" size={18} />
                    </Link>
                  )}

                  {hasPermission("back_office.menu_management.delete_course") && item?.status !== "archived" && (
                    <span>
                      {item?.status === "draft" ? (
                        <Trash2
                          onClick={() => {
                            setItemDialog(item);
                            setDeleteDialog(true);
                          }}
                          className="text-cred cursor-pointer"
                          size={18}
                        />
                      ) : (
                        <Archive
                          onClick={() => {
                            setArchiveActionType("archive");
                            setItemDialog(item);
                            setArchiveDialog(true);
                          }}
                          className="text-cred cursor-pointer"
                          size={18}
                        />
                      )}
                    </span>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={10} className="text-center">
              No data available
            </TableCell>
          </TableRow>
        )}
      </TableBody>

      {/* dialog */}
      <ArchiveConfirmDialog
        isLoading={updateLoading}
        isOpen={archiveDialog}
        onClose={closeArchiveDialog}
        onConfirm={() => {
          archiveHandler(itemDialog?.id, itemDialog, archiveActionType);
        }}
        title={archiveActionType === "archive" ? "Archive Course?" : "Restore Course?"}
        description={
          archiveActionType === "archive"
            ? `Are you sure you want to archive "${itemDialog?.name}"?`
            : `Are you sure you want to restore "${itemDialog?.name}"?`
        }
        isArchive={archiveActionType === "archive"}
      />
      <DeleteConfirmDialog
        isLoading={deleteLoading}
        isOpen={deleteDialog}
        onClose={() => {
          setDeleteDialog(false);
          setItemDialog(null);
        }}
        onConfirm={() => {
          deleteHandler(itemDialog?.id);
        }}
        title="Delete Draft Course?"
        description={`Are you sure you want to delete the draft course "${itemDialog?.name}"? This action cannot be undone.`}
      />

      <ImageDialog
        items={itemDialog}
        isOpen={comboImageDialog}
        onClose={closeImageDialog}
      />

      <ItemsDialog
        items={itemDialog?.course_items}
        isOpen={itemDetails}
        onClose={closeItemDetails}
      />
      <DetailsDialog
        items={itemDialog}
        isOpen={comboMealDetails}
        onClose={closeComboMealDetails}
      />
    </Table>
  );
}

export default CourseListTable;
